import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, RefreshCw, Users } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import EmptyState from '../components/EmptyState';
import DuplicateGroupCard from '../components/DuplicateGroupCard';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';

const COUNTERS = [
  ['total', 'dupes.counter_total'],
  ['system', 'dupes.counter_system'],
  ['manual_merge', 'dupes.counter_manual'],
  ['likely_different', 'dupes.counter_likely_different'],
  ['resolved', 'dupes.counter_resolved'],
];

export default function DuplicateCustomersPage() {
  const { t } = useLang();
  const qc = useQueryClient();
  const [status, setStatus] = useState('pending');
  const [caseType, setCaseType] = useState('all');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState([]);
  const [bulkPlan, setBulkPlan] = useState(null);
  const [job, setJob] = useState(null);

  const groupsQuery = useQuery({
    queryKey: ['dupe-groups', { status, caseType, q }],
    queryFn: async () => (await api.get('/admin/customers/duplicate-groups', {
      params: { status, case_type: caseType, q },
    })).data,
  });

  const groups = groupsQuery.data?.groups || [];
  const counters = groupsQuery.data?.counters || {};

  const selectable = useMemo(
    () => groups.filter((g) => g.resolution_type === 'system' && g.status === 'pending'),
    [groups]
  );

  const toggle = (id) => setSelected((s) => (
    s.includes(id) ? s.filter((x) => x !== id) : [...s, id]
  ));

  const bulkDry = useMutation({
    mutationFn: async () => (await api.post(
      '/admin/customers/duplicate-groups/bulk-resolve',
      { group_ids: selected, dry_run: true })).data,
    onSuccess: (d) => setBulkPlan(d),
    onError: () => toast.error(t('dupes.error_generic')),
  });

  const pollJob = async () => {
    for (let i = 0; i < 150; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const st = (await api.get('/admin/customers/duplicate-groups/bulk-resolve/status')).data;
      setJob(st);
      if (st.status !== 'running') return st;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 2000));
    }
    return null;
  };

  const bulkApply = useMutation({
    mutationFn: async () => {
      await api.post('/admin/customers/duplicate-groups/bulk-resolve',
        { group_ids: selected, dry_run: false });
      return pollJob();
    },
    onSuccess: (st) => {
      const rep = st?.report || {};
      toast.success(t('dupes.bulk_done')
        .replace('{{ok}}', rep.resolved ?? 0)
        .replace('{{fail}}', rep.failed ?? 0));
      setBulkPlan(null);
      setSelected([]);
      qc.invalidateQueries({ queryKey: ['dupe-groups'] });
    },
    onError: (err) => {
      const d = err?.response?.data?.detail;
      const code = typeof d === 'string' ? d : d?.reason;
      toast.error(t(`dupes.error_${code}`) === `dupes.error_${code}`
        ? (code || t('dupes.error_generic'))
        : t(`dupes.error_${code}`));
    },
  });

  return (
    <div data-testid="duplicate-customers-page">
      <PageHeader
        title={t('dupes.title')}
        subtitle={t('dupes.subtitle')}
        actions={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => qc.invalidateQueries({ queryKey: ['dupe-groups'] })}
            data-testid="dupes-refresh"
          >
            <RefreshCw className="h-4 w-4 mr-2" /> {t('common.refresh')}
          </Button>
        }
      />

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-5" data-testid="dupes-counters">
        {COUNTERS.map(([key, label]) => (
          <Card key={key} className="bg-card border-border">
            <CardContent className="p-3.5">
              <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                {t(label)}
              </div>
              <div
                className="mt-1 text-2xl font-semibold tabular-nums text-foreground"
                data-testid={`dupes-counter-${key}`}
              >
                {counters[key] ?? 0}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">
          {t('common.status')}
        </Label>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" data-testid="dupes-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">{t('dupes.status_pending')}</SelectItem>
            <SelectItem value="resolved">{t('dupes.status_resolved')}</SelectItem>
            <SelectItem value="ignored">{t('dupes.status_ignored')}</SelectItem>
            <SelectItem value="all">{t('dupes.status_all')}</SelectItem>
          </SelectContent>
        </Select>

        <Label className="text-xs uppercase tracking-wider text-muted-foreground">
          {t('common.type')}
        </Label>
        <Select value={caseType} onValueChange={setCaseType}>
          <SelectTrigger className="w-56" data-testid="dupes-type-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('dupes.type_all')}</SelectItem>
            <SelectItem value="system">{t('dupes.type_system')}</SelectItem>
            <SelectItem value="manual_merge">{t('dupes.type_manual')}</SelectItem>
            <SelectItem value="likely_different">{t('dupes.type_likely_different')}</SelectItem>
          </SelectContent>
        </Select>

        <Input
          className="w-64"
          placeholder={t('dupes.search_placeholder')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="dupes-search"
        />

        <div className="ml-auto flex items-center gap-2">
          {selectable.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelected(
                selected.length === selectable.length
                  ? []
                  : selectable.map((g) => g.group_id)
              )}
              data-testid="dupes-select-all"
            >
              {selected.length === selectable.length
                ? t('dupes.clear_selection')
                : t('dupes.select_all_system')}
            </Button>
          )}
          <Button
            size="sm"
            disabled={selected.length === 0 || bulkDry.isPending}
            onClick={() => bulkDry.mutate()}
            data-testid="dupes-bulk-button"
          >
            {bulkDry.isPending
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Users className="h-4 w-4 mr-2" />}
            {t('dupes.bulk_resolve').replace('{{n}}', selected.length)}
          </Button>
        </div>
      </div>

      {groupsQuery.isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : groups.length === 0 ? (
        <EmptyState message={t('dupes.empty')} testid="dupes-empty" />
      ) : (
        <div className="space-y-3" data-testid="dupes-groups">
          {groups.map((g) => (
            <DuplicateGroupCard
              key={g.group_id}
              group={g}
              selected={selected.includes(g.group_id)}
              onToggleSelect={toggle}
            />
          ))}
        </div>
      )}

      <Dialog open={!!bulkPlan} onOpenChange={(v) => { if (!v) setBulkPlan(null); }}>
        <DialogContent data-testid="dupes-bulk-dialog">
          <DialogHeader>
            <DialogTitle>{t('dupes.bulk_confirm_title')}</DialogTitle>
          </DialogHeader>
          <p className="text-[12px] leading-relaxed" data-testid="dupes-bulk-summary">
            {t('dupes.bulk_summary')
              .replace('{{groups}}', bulkPlan?.groups ?? 0)
              .replace('{{keep}}', bulkPlan?.keep_count ?? 0)
              .replace('{{archive}}', bulkPlan?.archive_count ?? 0)}
          </p>
          {job?.status === 'running' && (
            <p className="text-[11px] text-muted-foreground" data-testid="dupes-bulk-progress">
              {job?.progress?.done ?? 0} / {job?.progress?.total ?? 0}
            </p>
          )}
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setBulkPlan(null)} data-testid="dupes-bulk-cancel">
              {t('common.cancel')}
            </Button>
            <Button
              size="sm"
              disabled={bulkApply.isPending || !bulkPlan?.groups}
              onClick={() => bulkApply.mutate()}
              data-testid="dupes-bulk-confirm"
            >
              {bulkApply.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('dupes.confirm_apply')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
